import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiType,
  FiList,
  FiSend,
  FiChevronDown,
  FiCalendar,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiAlignJustify,
  FiMinus,
  FiPlus,
  FiSearch,
  FiCheck,
  FiX,
  FiArrowLeft,
  FiUpload,
  FiPaperclip,
  FiMapPin,
  FiLink,
  FiRotateCcw,
  FiRotateCw,
  FiTrash2,
} from 'react-icons/fi';
import { ScheduleModal, ModalBackdrop, ModalContentWrapper } from './Modals';
import { EventPoster } from './EventPoster';
import { toast } from '../../utils/toast';
import { parseMailContactsCsvFile } from '../../utils/mailCsv';
import {
  validateBroadcastAttachment,
  readFileAsBase64,
  getBroadcastAttachAcceptAttribute,
} from '../../utils/broadcastAttachment';
import {
  useGetMailingLists,
  useGetBroadcastEmail,
  useGetBroadcastAttachmentContent,
  useCreateBroadcast,
  useUpdateBroadcast,
  useCreateMailingList,
  useAddContactsToMailingList,
} from '../../api/mailing-lists';
import {
  useGetProfileEvents,
  isEventActive,
  getEventLocation,
  type EventItem,
} from '../../api/events';
import type { MailingList, CreateBroadcastPayload, AttachmentPayload } from './types';

interface ComposePageProps {
  preselectedListUuid?: string;
  preselectedDraftUuid?: string;
}

export const ComposePage: React.FC<ComposePageProps> = ({
  preselectedListUuid,
  preselectedDraftUuid,
}) => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [to, setTo] = useState('');
  const [body, setBody] = useState('');
  const [showRecipients, setShowRecipients] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [selectedListUuid, setSelectedListUuid] = useState<string | null>(
    preselectedListUuid ?? null,
  );
  const [recipientSearch, setRecipientSearch] = useState('');
  const [editingBroadcastUuid] = useState<string | null>(preselectedDraftUuid ?? null);

  // Attachment state
  const [attachment, setAttachment] = useState<AttachmentPayload | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);

  // Fetch existing draft data when editing
  const { data: editBroadcastData, isPlaceholderData: isDraftPlaceholder } = useGetBroadcastEmail(
    preselectedListUuid,
    preselectedDraftUuid,
  );

  const draftAttachmentName = editBroadcastData?.data?.attachments?.[0]?.name;

  const { data: attachmentContentData, isLoading: isLoadingAttachmentContent } =
    useGetBroadcastAttachmentContent(
      preselectedListUuid,
      preselectedDraftUuid,
      !!draftAttachmentName && !isDraftPlaceholder,
    );

  // Pre-fill form when editing a draft
  React.useEffect(() => {
    if (editBroadcastData?.data && preselectedDraftUuid) {
      const draft = editBroadcastData.data;
      setSubject(draft.subject || '');
      setBody(draft.body || '');
      if (preselectedListUuid) {
        setSelectedListUuid(preselectedListUuid);
      }
    }
  }, [editBroadcastData, preselectedDraftUuid, preselectedListUuid]);

  // Lazy-load attachment content when editing a draft
  React.useEffect(() => {
    if (draftAttachmentName && attachmentContentData?.content) {
      setAttachment({ name: draftAttachmentName, content: attachmentContentData.content });
    }
  }, [draftAttachmentName, attachmentContentData]);

  const handleAttachFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setAttachError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateBroadcastAttachment(file);
    if (validationError) {
      setAttachError(validationError);
      e.target.value = '';
      return;
    }

    try {
      const base64 = await readFileAsBase64(file);
      setAttachment({ content: base64, name: file.name });
    } catch {
      setAttachError('Failed to read the selected file. Please try again.');
    } finally {
      e.target.value = '';
    }
  };

  // Rich Text Editor states & refs
  const editorRef = useRef<HTMLDivElement>(null);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrike, setIsStrike] = useState(false);
  const [alignment, setAlignment] = useState<'left' | 'center' | 'right' | 'justify'>('left');
  const [listType, setListType] = useState<'none' | 'bullet' | 'ordered'>('none');
  const [fontSize, setFontSize] = useState('3'); // HTML size 3 = 14px
  const [fontFamily, setFontFamily] = useState('Arial');
  const [blockType, setBlockType] = useState('p');
  const [textColor, setTextColor] = useState('#ffffff');
  const [zoom, setZoom] = useState('100%');

  const FONT_FAMILIES = [
    { label: 'Arial', value: 'Arial' },
    { label: 'Helvetica', value: 'Helvetica' },
    { label: 'Georgia', value: 'Georgia' },
    { label: 'Times New Roman', value: 'Times New Roman' },
    { label: 'Verdana', value: 'Verdana' },
    { label: 'Courier New', value: 'Courier New' },
  ];

  const BLOCK_TYPES = [
    { label: 'Paragraph text', value: 'p' },
    { label: 'Heading 1', value: 'h1' },
    { label: 'Heading 2', value: 'h2' },
    { label: 'Heading 3', value: 'h3' },
    { label: 'Quote', value: 'blockquote' },
  ];

  const FONT_SIZES = [
    { label: '10px', value: '1' },
    { label: '12px', value: '2' },
    { label: '14px', value: '3' },
    { label: '16px', value: '4' },
    { label: '18px', value: '5' },
    { label: '24px', value: '6' },
    { label: '32px', value: '7' },
  ];

  // Set initial content & handle external updates
  React.useEffect(() => {
    if (editorRef.current) {
      if (editorRef.current.innerHTML !== body) {
        if (document.activeElement !== editorRef.current || !body) {
          editorRef.current.innerHTML = body || '';
        }
      }
    }
  }, [body]);

  const detectBlockType = () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    let node: Node | null = sel.getRangeAt(0).commonAncestorContainer;
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = (node as HTMLElement).tagName.toLowerCase();
        if (['h1', 'h2', 'h3', 'p', 'blockquote'].includes(tag)) {
          setBlockType(tag);
          return;
        }
        if (tag === 'div') {
          setBlockType('p');
          return;
        }
      }
      node = node.parentNode;
    }
    setBlockType('p');
  };

  const updateToolbarStates = () => {
    setIsBold(document.queryCommandState('bold'));
    setIsItalic(document.queryCommandState('italic'));
    setIsUnderline(document.queryCommandState('underline'));
    setIsStrike(document.queryCommandState('strikeThrough'));

    if (document.queryCommandState('justifyLeft')) setAlignment('left');
    else if (document.queryCommandState('justifyCenter')) setAlignment('center');
    else if (document.queryCommandState('justifyRight')) setAlignment('right');
    else if (document.queryCommandState('justifyFull')) setAlignment('justify');

    if (document.queryCommandState('insertUnorderedList')) setListType('bullet');
    else if (document.queryCommandState('insertOrderedList')) setListType('ordered');
    else setListType('none');

    detectBlockType();
  };

  const executeCommand = (command: string, value: string = '') => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    updateToolbarStates();
    if (editorRef.current) {
      setBody(editorRef.current.innerHTML);
    }
  };

  const handleFontFamilyChange = (font: string) => {
    setFontFamily(font);
    executeCommand('fontName', font);
  };

  const handleBlockTypeChange = (type: string) => {
    setBlockType(type);
    executeCommand('formatBlock', `<${type}>`);
  };

  const handleInsertLink = () => {
    const url = window.prompt('Enter URL:', 'https://');
    if (!url) return;
    executeCommand('createLink', url);
  };

  const handleFontSizeChange = (sizeVal: string) => {
    setFontSize(sizeVal);
    executeCommand('fontSize', sizeVal);
  };

  const handleFontSizeStep = (increment: boolean) => {
    const currentIdx = FONT_SIZES.findIndex((s) => s.value === fontSize);
    let nextIdx = currentIdx;
    if (increment) {
      if (nextIdx < FONT_SIZES.length - 1) nextIdx++;
    } else {
      if (nextIdx > 0) nextIdx--;
    }
    const nextSize = FONT_SIZES[nextIdx];
    if (nextSize) {
      handleFontSizeChange(nextSize.value);
    }
  };

  const handleEditorInput = (e: React.FormEvent<HTMLDivElement>) => {
    setBody(e.currentTarget.innerHTML);
  };

  // Fetch real mailing lists
  const { data: listsData } = useGetMailingLists(1, 100);
  const lists: MailingList[] = React.useMemo(() => listsData?.results ?? [], [listsData?.results]);

  // Broadcast mutations — create new or update existing draft
  const createBroadcast = useCreateBroadcast(selectedListUuid ?? undefined);
  const updateBroadcast = useUpdateBroadcast(
    selectedListUuid ?? undefined,
    editingBroadcastUuid ?? undefined,
  );
  const broadcastMutation = editingBroadcastUuid ? updateBroadcast : createBroadcast;

  // Auto-select preselected list
  React.useEffect(() => {
    if (preselectedListUuid && lists.length > 0) {
      const list = lists.find((l) => l.uuid === preselectedListUuid);
      if (list) {
        setTo(list.name);
        setSelectedListUuid(list.uuid);
      }
    }
  }, [preselectedListUuid, lists]);

  const handleRecipientsConfirm = (listUuid: string, listName: string) => {
    setTo(listName);
    setSelectedListUuid(listUuid);
    setShowRecipients(false);
  };

  const handleSchedule = (date: Date) => {
    const payload: CreateBroadcastPayload = {
      subject,
      body,
      scheduled_at: date.toISOString(),
      ...(attachment ? { attachment: [attachment] } : {}),
    };
    broadcastMutation.mutate(payload, {
      onSuccess: () => {
        setShowSchedule(false);
        navigate({ to: '/mail' });
      },
    });
  };

  const handleSendNow = () => {
    if (!selectedListUuid) {
      setShowRecipients(true);
      return;
    }
    const payload: CreateBroadcastPayload = {
      subject,
      body,
      ...(editingBroadcastUuid ? { save_as_draft: false } : {}),
      ...(attachment ? { attachment: [attachment] } : {}),
    };
    broadcastMutation.mutate(payload, {
      onSuccess: () => {
        navigate({ to: '/mail' });
      },
    });
  };

  const handleSaveDraft = () => {
    if (!selectedListUuid) {
      setShowRecipients(true);
      return;
    }
    const payload: CreateBroadcastPayload = {
      subject,
      body,
      save_as_draft: true,
      ...(attachment ? { attachment: [attachment] } : {}),
    };
    broadcastMutation.mutate(payload, {
      onSuccess: () => {
        navigate({ to: '/mail' });
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Compose | Mail</title>
      </Helmet>

      <div className="flex-1 flex flex-col rounded-none md:rounded-[16px] border-0 md:border border-[#262626] bg-[#0E0E0E] overflow-hidden">
        {/* Title */}
        <div className="px-4 md:px-6 pt-3 md:pt-4 pb-2 flex items-center gap-3">
          <Link
            to="/mail"
            className="md:hidden p-1 rounded-lg hover:bg-white/5 text-[#A3A3A3] hover:text-white transition-colors"
          >
            <FiArrowLeft size={18} />
          </Link>
          <h2 className="text-sm md:text-base font-semibold text-white">Compose Email</h2>
        </div>

        {/* To field — shows selected list name */}
        <div className="px-3 md:px-4 pt-2 pb-2">
          <div className="flex items-center gap-2 h-11 md:h-[53px] px-3 md:px-5 rounded-xl md:rounded-[17.3px] bg-[#131317]/30 border border-white/10">
            <span className="text-[#A3A3A3] text-xs md:text-sm">
              To:<span className="text-red-400">*</span>
            </span>
            <input
              type="text"
              placeholder={to ? '' : 'Select a mailing list'}
              value={to}
              readOnly
              className="flex-1 bg-transparent text-white text-xs md:text-sm placeholder:text-[#A3A3A3] focus:outline-none cursor-pointer min-w-0"
              onClick={() => setShowRecipients(true)}
            />
            <button
              onClick={() => setShowRecipients(true)}
              className="flex items-center gap-1 md:gap-1.5 text-xs md:text-sm font-medium text-[#2F80ED] hover:text-[#4A9AF0] transition-colors flex-shrink-0"
            >
              <FiUsersIcon />
              <span className="hidden sm:inline">Choose recipients</span>
            </button>
          </div>
        </div>

        {/* Subject field */}
        <div className="px-3 md:px-4 pb-2">
          <div className="flex items-center gap-2 h-11 md:h-[53px] px-3 md:px-5 rounded-xl md:rounded-[17.3px] bg-[#131317]/30 border border-white/10">
            <span className="text-[#A3A3A3] text-xs md:text-sm">
              Subject:<span className="text-red-400">*</span>
            </span>
            <input
              type="text"
              placeholder="Write a subject line"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="flex-1 bg-transparent text-white text-xs md:text-sm placeholder:text-[#525252] focus:outline-none min-w-0"
            />
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 flex flex-col mx-3 md:mx-4 mb-3 md:mb-4 rounded-lg md:rounded-xl bg-[#0D0D0D] border border-[#262626] overflow-hidden min-h-[200px]">
          {/* Custom style overrides for placeholder and list styling */}
          <style
            dangerouslySetInnerHTML={{
              __html: `
            .rich-editor[contenteditable]:empty:before {
              content: attr(placeholder);
              color: #525252;
              cursor: text;
            }
            .rich-editor h1 {
              font-size: 2rem !important;
              font-weight: 700 !important;
              line-height: 1.2 !important;
              margin: 0.75rem 0 0.5rem 0 !important;
              color: #ffffff !important;
            }
            .rich-editor h2 {
              font-size: 1.5rem !important;
              font-weight: 700 !important;
              line-height: 1.3 !important;
              margin: 0.6rem 0 0.4rem 0 !important;
              color: #ffffff !important;
            }
            .rich-editor h3 {
              font-size: 1.25rem !important;
              font-weight: 600 !important;
              line-height: 1.4 !important;
              margin: 0.5rem 0 0.4rem 0 !important;
              color: #ffffff !important;
            }
            .rich-editor p {
              font-size: 0.875rem !important;
              line-height: 1.6 !important;
              margin: 0.4rem 0 !important;
            }
            .rich-editor ul {
              list-style-type: disc !important;
              padding-left: 1.5rem !important;
              margin-top: 0.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            .rich-editor ol {
              list-style-type: decimal !important;
              padding-left: 1.5rem !important;
              margin-top: 0.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            .rich-editor blockquote {
              border-left: 4px solid #333333 !important;
              padding-left: 1rem !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              color: #a3a3a3 !important;
              font-style: italic !important;
            }
            .rich-editor a {
              color: #2F80ED !important;
              text-decoration: underline !important;
            }
          `,
            }}
          />

          {/* Toolbar */}
          <div className="flex items-center gap-0.5 md:gap-1 px-2 md:px-3 h-11 md:h-[50px] border-b border-[#262626] bg-[#0D0D0D] overflow-x-auto scrollbar-hide">
            <div className="hidden md:block">
              <ToolbarSelect
                value={zoom}
                onChange={setZoom}
                options={[
                  { label: '50%', value: '50%' },
                  { label: '75%', value: '75%' },
                  { label: '100%', value: '100%' },
                  { label: '125%', value: '125%' },
                  { label: '150%', value: '150%' },
                  { label: '200%', value: '200%' },
                ]}
              />
            </div>
            <ToolbarSelect
              value={blockType}
              onChange={handleBlockTypeChange}
              options={BLOCK_TYPES}
            />
            <div className="hidden md:block">
              <ToolbarSelect
                value={fontFamily}
                onChange={handleFontFamilyChange}
                options={FONT_FAMILIES}
              />
            </div>
            <div className="w-px h-5 bg-[#333333] mx-0.5 md:mx-1 flex-shrink-0" />
            <div className="flex items-center bg-transparent rounded text-[#D1D1D1] hover:text-white transition-colors flex-shrink-0">
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleFontSizeStep(false);
                }}
                className="p-1.5 hover:bg-white/5 rounded-l transition-colors"
                title="Decrease font size"
                type="button"
              >
                <FiMinus size={14} />
              </button>
              <span className="text-xs px-1 font-medium min-w-[28px] text-center">
                {FONT_SIZES.find((s) => s.value === fontSize)?.label || '14px'}
              </span>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleFontSizeStep(true);
                }}
                className="p-1.5 hover:bg-white/5 rounded-r transition-colors"
                title="Increase font size"
                type="button"
              >
                <FiPlus size={14} />
              </button>
            </div>
            <div className="w-px h-5 bg-[#333333] mx-1 flex-shrink-0" />
            <ToolbarButton
              icon={FiBold}
              title="Bold"
              isActive={isBold}
              onClick={() => executeCommand('bold')}
            />
            <ToolbarButton
              icon={FiItalic}
              title="Italic"
              isActive={isItalic}
              onClick={() => executeCommand('italic')}
            />
            <ToolbarButton
              icon={FiUnderline}
              title="Underline"
              isActive={isUnderline}
              onClick={() => executeCommand('underline')}
            />
            <ToolbarButton
              icon={FiType}
              title="Strikethrough"
              isActive={isStrike}
              onClick={() => executeCommand('strikeThrough')}
            />

            {/* Text color picker */}
            <div
              className="relative w-8 h-8 flex items-center justify-center rounded hover:bg-white/5 cursor-pointer flex-shrink-0"
              title="Text color"
            >
              <input
                type="color"
                value={textColor}
                onChange={(e) => {
                  setTextColor(e.target.value);
                  executeCommand('foreColor', e.target.value);
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div
                className="w-4 h-4 rounded border border-white/20"
                style={{ backgroundColor: textColor }}
              />
            </div>

            <div className="w-px h-5 bg-[#333333] mx-0.5 md:mx-1 flex-shrink-0" />
            <ToolbarButton
              icon={FiAlignLeft}
              title="Align left"
              isActive={alignment === 'left'}
              onClick={() => executeCommand('justifyLeft')}
            />
            <ToolbarButton
              icon={FiAlignCenter}
              title="Align center"
              isActive={alignment === 'center'}
              onClick={() => executeCommand('justifyCenter')}
            />
            <ToolbarButton
              icon={FiAlignRight}
              title="Align right"
              isActive={alignment === 'right'}
              onClick={() => executeCommand('justifyRight')}
            />
            <ToolbarButton
              icon={FiAlignJustify}
              title="Justify"
              isActive={alignment === 'justify'}
              onClick={() => executeCommand('justifyFull')}
            />
            <div className="w-px h-5 bg-[#333333] mx-0.5 md:mx-1 flex-shrink-0" />
            <ToolbarButton
              icon={FiList}
              title="Bullet list"
              isActive={listType === 'bullet'}
              onClick={() => executeCommand('insertUnorderedList')}
            />
            <ToolbarButton
              icon={ListOrderedIcon}
              title="Numbered list"
              isActive={listType === 'ordered'}
              onClick={() => executeCommand('insertOrderedList')}
            />
            <div className="w-px h-5 bg-[#333333] mx-0.5 md:mx-1 flex-shrink-0" />
            <ToolbarButton icon={FiLink} title="Insert link" onClick={handleInsertLink} />
            <ToolbarButton
              icon={FiTrash2}
              title="Remove formatting"
              onClick={() => executeCommand('removeFormat')}
            />
            <div className="w-px h-5 bg-[#333333] mx-0.5 md:mx-1 flex-shrink-0" />
            <ToolbarButton icon={FiRotateCcw} title="Undo" onClick={() => executeCommand('undo')} />
            <ToolbarButton icon={FiRotateCw} title="Redo" onClick={() => executeCommand('redo')} />
          </div>

          {/* Content Area with Zoom */}
          <div className="flex-1 overflow-y-auto bg-[#0D0D0D]">
            <div
              style={{
                zoom: parseFloat(zoom) / 100,
                transformOrigin: 'top left',
              }}
              className="w-full h-full min-h-[300px]"
            >
              <div
                ref={editorRef}
                contentEditable
                // @ts-expect-error placeholder is not standard for div but used for CSS placeholder styles
                placeholder="Start writing your email..."
                onInput={handleEditorInput}
                onKeyUp={updateToolbarStates}
                onMouseUp={updateToolbarStates}
                onFocus={updateToolbarStates}
                className="rich-editor w-full h-full px-3 md:px-5 py-3 md:py-4 bg-transparent text-white text-sm focus:outline-none min-h-[200px] md:min-h-[300px] prose prose-invert max-w-none"
              />
            </div>
          </div>
        </div>

        {/* Bottom actions */}
        <div className="flex flex-col gap-2 px-3 md:px-4 pb-3 md:pb-4">
          {/* Attachment chip — visible when a file is attached */}
          {attachment && (
            <div className="flex items-center gap-2 self-start">
              <div className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full bg-[#2F80ED]/10 border border-[#2F80ED]/25 text-xs text-[#2F80ED] font-medium max-w-[280px] md:max-w-[320px]">
                <FiPaperclip size={12} className="flex-shrink-0" />
                <span className="truncate max-w-[180px] md:max-w-[220px]" title={attachment.name}>
                  {attachment.name}
                </span>
                <button
                  onClick={() => setAttachment(null)}
                  className="ml-1 flex-shrink-0 text-[#2F80ED]/60 hover:text-[#F87171] transition-colors"
                  title="Remove attachment"
                >
                  <FiX size={12} />
                </button>
              </div>
            </div>
          )}

          {/* Loading attachment content for draft restore */}
          {!attachment && draftAttachmentName && isLoadingAttachmentContent && (
            <div className="flex items-center gap-2 self-start">
              <div className="flex items-center gap-2 pl-3 pr-3 py-1.5 rounded-full bg-white/5 border border-[#262626] text-xs text-[#737373] max-w-[280px] md:max-w-[320px]">
                <FiPaperclip size={12} className="flex-shrink-0" />
                <span
                  className="truncate max-w-[180px] md:max-w-[220px]"
                  title={draftAttachmentName}
                >
                  {draftAttachmentName}
                </span>
                <span className="flex-shrink-0 inline-block w-2.5 h-2.5 border-2 border-[#525252] border-t-transparent rounded-full animate-spin" />
              </div>
            </div>
          )}

          {/* Error message */}
          {attachError && <p className="text-xs text-[#F87171] self-start">{attachError}</p>}

          {/* Mobile: stacked layout, Desktop: row layout */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            {/* Attach file button */}
            <div className="flex items-center gap-2">
              <input
                ref={attachInputRef}
                type="file"
                onChange={handleAttachFile}
                className="hidden"
                accept={getBroadcastAttachAcceptAttribute()}
              />
              <button
                onClick={() => attachInputRef.current?.click()}
                disabled={!!attachment}
                className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-full border border-[#333333] text-xs md:text-sm text-[#A3A3A3] hover:text-white hover:border-white/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={
                  attachment
                    ? 'Remove the current attachment first'
                    : 'Attach a GIF, JPEG, or PNG image (max 4 MB)'
                }
              >
                <FiPaperclip size={14} className="md:hidden" />
                <FiPaperclip size={15} className="hidden md:block" />
                <span className="hidden sm:inline">{attachment ? 'Attached' : 'Attach File'}</span>
                <span className="sm:hidden">{attachment ? 'Attached' : 'Attach'}</span>
              </button>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              {/* Save Draft */}
              <button
                onClick={handleSaveDraft}
                disabled={broadcastMutation.isPending}
                className="flex-1 md:flex-none px-3 md:px-4 py-2 md:py-2.5 rounded-full border border-[#333333] text-xs md:text-sm text-[#A3A3A3] hover:text-white hover:border-white/30 transition-colors disabled:opacity-40"
              >
                {broadcastMutation.isPending ? 'Saving...' : 'Save Draft'}
              </button>

              {/* Schedule button — always visible */}
              <button
                onClick={() => setShowSchedule(true)}
                disabled={broadcastMutation.isPending || !subject.trim() || !body.trim()}
                className="flex items-center justify-center gap-1.5 md:gap-2 flex-1 md:flex-none px-3 md:px-4 py-2 md:py-2.5 rounded-full border border-[#2F80ED]/40 text-xs md:text-sm text-[#2F80ED] font-medium hover:bg-[#2F80ED]/10 hover:border-[#2F80ED]/60 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FiCalendar size={14} className="md:hidden" />
                <FiCalendar size={15} className="hidden md:block" />
                <span className="hidden sm:inline">Schedule</span>
              </button>

              {/* Send now button */}
              <button
                onClick={handleSendNow}
                disabled={broadcastMutation.isPending || !subject.trim() || !body.trim()}
                className="flex items-center justify-center gap-1.5 md:gap-2 flex-1 md:flex-none px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-white text-black text-xs md:text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-40"
              >
                <FiSend size={14} className="md:hidden" />
                <FiSend size={15} className="hidden md:block" />
                <span>{broadcastMutation.isPending ? 'Sending...' : 'Send'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Recipients Modal — shows real mailing lists */}
      {showRecipients && (
        <RecipientsModal
          lists={lists}
          selectedUuid={selectedListUuid}
          onClose={() => setShowRecipients(false)}
          onConfirm={handleRecipientsConfirm}
          search={recipientSearch}
          onSearchChange={setRecipientSearch}
        />
      )}

      {showSchedule && (
        <ScheduleModal onClose={() => setShowSchedule(false)} onSchedule={handleSchedule} />
      )}
    </>
  );
};

// ─── Recipients Modal (API-driven) ──────────────────────────────────────────

interface RecipientsModalProps {
  lists: MailingList[];
  selectedUuid: string | null;
  onClose: () => void;
  onConfirm: (listUuid: string, listName: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
}

const RecipientsModal: React.FC<RecipientsModalProps> = ({
  lists,
  selectedUuid,
  onClose,
  onConfirm,
  search,
  onSearchChange,
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'events' | 'custom'>('all');
  const [isCreatingNewList, setIsCreatingNewList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListEmails, setNewListEmails] = useState('');
  const [nameError, setNameError] = useState('');
  const [recipientsError, setRecipientsError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
  const [eventSearch, setEventSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const createListMutation = useCreateMailingList();
  const addContactsToDynamicList = useAddContactsToMailingList();

  const userId = typeof window !== 'undefined' ? localStorage.getItem('user_id') : undefined;
  const { data: profileEventsData, isLoading: eventsLoading } = useGetProfileEvents(
    userId ?? undefined,
    1,
    100,
  );

  const allEvents = profileEventsData?.results ?? [];
  const activePublishedEvents = allEvents.filter(
    (ev) => isEventActive(ev) && ev.is_draft_or_published === 'published',
  );

  const searchLower = eventSearch.trim().toLowerCase();
  const filteredEvents = searchLower
    ? activePublishedEvents.filter(
        (ev) =>
          (ev.event_title ?? '').toLowerCase().includes(searchLower) ||
          (ev.venue ?? ev.location ?? '').toLowerCase().includes(searchLower),
      )
    : activePublishedEvents;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setEventDropdownOpen(false);
      }
    }
    if (eventDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [eventDropdownOpen]);

  const handleSelectEvent = (ev: EventItem | null) => {
    setSelectedEvent(ev);
    setEventDropdownOpen(false);
    setEventSearch('');
  };

  const allCount = lists.length;
  const eventsCount = lists.filter((l) => l.is_event_linked).length;
  const customCount = lists.filter((l) => !l.is_event_linked).length;

  const filtered = lists.filter((l) => {
    const matchesTab =
      activeTab === 'all' ? true : activeTab === 'events' ? l.is_event_linked : !l.is_event_linked;
    const matchesSearch = l.name.toLowerCase().includes(search.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleCsvImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    void parseMailContactsCsvFile(file).then((result) => {
      if (result.errors.length > 0) {
        toast.error(result.errors[0]);
        return;
      }

      const importedEmails = result.contacts.map((contact) => contact.email);
      if (importedEmails.length === 0) {
        toast.error('No valid emails found in CSV.');
        return;
      }

      setNewListEmails((prev) => {
        const currentList = prev
          .split(/[\n,;]/)
          .map((x) => x.trim())
          .filter(Boolean);

        const seen = new Set<string>();
        const combined: string[] = [];

        [...currentList, ...importedEmails].forEach((email) => {
          const normalized = email.toLowerCase();
          if (!seen.has(normalized)) {
            seen.add(normalized);
            combined.push(email);
          }
        });

        return combined.join(', ');
      });

      if (result.summary.skippedRows > 0) {
        toast.warn(
          `Imported ${result.summary.validRows} contacts, skipped ${result.summary.skippedRows} rows.`,
        );
      } else {
        toast.success(`Imported ${result.summary.validRows} contacts`);
      }
    });

    e.target.value = '';
  };

  if (isCreatingNewList) {
    return (
      <ModalBackdrop onClose={() => setIsCreatingNewList(false)}>
        <ModalContentWrapper
          onClose={() => setIsCreatingNewList(false)}
          maxWidthClass="max-w-[600px]"
        >
          <div className="flex items-center justify-between px-6 h-[72px] border-b border-[#262626] shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsCreatingNewList(false)}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors cursor-pointer text-[#737373] hover:text-white"
              >
                <FiArrowLeft size={20} />
              </button>
              <h2 className="text-xl font-semibold text-white">New List</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/5 transition-colors text-[#737373] hover:text-white"
            >
              <FiX size={20} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-5 text-white">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter Label"
                value={newListName}
                onChange={(e) => {
                  setNewListName(e.target.value);
                  setNameError('');
                }}
                className={`w-full h-12 px-4 rounded-xl bg-transparent border text-sm text-white placeholder-[#737373] focus:outline-none transition-colors ${nameError ? 'border-red-500/50 focus:border-red-400' : 'border-[#262626] focus:border-[#333]'}`}
                autoFocus
              />
              {nameError && <p className="text-xs text-red-400 mt-1.5">{nameError}</p>}
            </div>

            {/* Attach Event Dropdown */}
            <div ref={dropdownRef} className="relative">
              <label className="block text-sm font-medium text-white mb-2">
                Attach Event (optional)
              </label>
              <button
                type="button"
                onClick={() => setEventDropdownOpen((prev) => !prev)}
                className="w-full h-12 px-4 rounded-xl bg-transparent border border-[#262626] text-sm text-white text-left flex items-center justify-between focus:outline-none focus:border-[#333] transition-colors hover:border-[#333]"
              >
                <span className={selectedEvent ? 'text-white' : 'text-[#737373]'}>
                  {selectedEvent
                    ? (selectedEvent.event_title ?? 'Untitled Event')
                    : 'Select an event...'}
                </span>
                <FiChevronDown
                  size={16}
                  className={`text-[#737373] transition-transform duration-200 ${
                    eventDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {eventDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-[#1a1a1a] border border-[#333] rounded-xl shadow-2xl overflow-hidden">
                  <div className="p-3 border-b border-[#262626]">
                    <div className="relative flex items-center">
                      <FiSearch
                        size={14}
                        className="absolute left-3 text-[#737373] pointer-events-none"
                      />
                      <input
                        type="text"
                        value={eventSearch}
                        onChange={(e) => setEventSearch(e.target.value)}
                        placeholder="Search events..."
                        className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-9 pr-3 h-9 text-white text-xs placeholder:text-[#737373] focus:outline-none focus:border-[#555] transition-colors"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="max-h-[220px] overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => handleSelectEvent(null)}
                      className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${
                        !selectedEvent
                          ? 'bg-blue-500/10 text-blue-400'
                          : 'text-[#737373] hover:bg-white/[0.04] hover:text-white'
                      }`}
                    >
                      <span className="w-3" />
                      None
                    </button>

                    {eventsLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                      </div>
                    ) : filteredEvents.length === 0 ? (
                      <p className="text-[#737373] text-xs text-center py-6 px-4">
                        {eventSearch ? 'No matching events' : 'No active events'}
                      </p>
                    ) : (
                      filteredEvents.map((ev) => {
                        const isSelected = selectedEvent?.id === ev.id;
                        const date = formatDate(ev.start);
                        const location = getEventLocation(ev);

                        return (
                          <button
                            key={ev.id}
                            type="button"
                            onClick={() => handleSelectEvent(ev)}
                            className={`w-full px-4 py-2.5 text-left transition-colors ${
                              isSelected ? 'bg-blue-500/10' : 'hover:bg-white/[0.04]'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 border-2 transition-colors ${
                                  isSelected ? 'bg-blue-500 border-blue-500' : 'border-[#555]'
                                }`}
                              />
                              <div className="min-w-0">
                                <p
                                  className={`text-sm font-medium truncate ${
                                    isSelected ? 'text-blue-400' : 'text-white'
                                  }`}
                                >
                                  {ev.event_title ?? 'Untitled Event'}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-[#737373]">
                                  {date && (
                                    <span className="flex items-center gap-1">
                                      <FiCalendar size={10} />
                                      {date}
                                    </span>
                                  )}
                                  {location && (
                                    <span className="flex items-center gap-1 truncate">
                                      <FiMapPin size={10} />
                                      <span className="truncate">{location}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-white">
                  Emails <span className="text-red-400">*</span>
                </label>
                <button
                  onClick={handleImportClick}
                  className="flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#171717] hover:bg-white/10 text-xs font-semibold text-white border border-[#262626] transition-colors cursor-pointer"
                >
                  <FiUpload size={12} className="text-[#2F80ED]" />
                  Import list (csv)
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleCsvImport}
                  accept=".csv"
                  className="hidden"
                />
              </div>
              <div className="mb-2 text-right">
                <a
                  href="/mail_contacts_sample.csv"
                  download
                  className="text-[11px] text-[#737373] hover:text-white transition-colors"
                >
                  Download sample CSV
                </a>
              </div>
              <textarea
                placeholder="name@example.com, separate with commas"
                value={newListEmails}
                onChange={(e) => {
                  setNewListEmails(e.target.value);
                  setRecipientsError('');
                }}
                className={`w-full h-32 p-4 rounded-xl bg-transparent border text-sm text-white placeholder-[#737373] focus:outline-none transition-colors resize-none ${recipientsError ? 'border-red-500/50 focus:border-red-400' : 'border-[#262626] focus:border-[#333]'}`}
              />
              {recipientsError && <p className="text-xs text-red-400 mt-1.5">{recipientsError}</p>}
            </div>
          </div>
          <div className="flex justify-end px-6 py-4 border-t border-[#262626] shrink-0 bg-[#0E0E0E]">
            <button
              onClick={() => {
                let hasError = false;
                if (!newListName.trim()) {
                  setNameError('List name is required.');
                  hasError = true;
                } else {
                  setNameError('');
                }
                const emailList = newListEmails
                  .split(/[\n,;]/)
                  .map((x) => x.trim())
                  .filter(Boolean);
                if (emailList.length === 0) {
                  setRecipientsError('Add at least one recipient email.');
                  hasError = true;
                } else {
                  setRecipientsError('');
                }
                if (hasError) return;
                createListMutation.mutate(
                  { name: newListName.trim(), event_id: selectedEvent?.id },
                  {
                    onSuccess: (response) => {
                      const newListUuid = response.data?.uuid;
                      const emailList = newListEmails
                        .split(/[\n,;]/)
                        .map((x) => x.trim())
                        .filter(Boolean);

                      if (newListUuid && emailList.length > 0) {
                        addContactsToDynamicList.mutate(
                          {
                            listUuid: newListUuid,
                            payload: { contacts: emailList.map((email) => ({ email })) },
                          },
                          {
                            onSuccess: () => {
                              onConfirm(newListUuid, newListName.trim());
                              setIsCreatingNewList(false);
                            },
                          },
                        );
                      } else if (newListUuid) {
                        onConfirm(newListUuid, newListName.trim());
                        setIsCreatingNewList(false);
                      }
                    },
                  },
                );
              }}
              disabled={
                !newListName.trim() ||
                createListMutation.isPending ||
                addContactsToDynamicList.isPending
              }
              className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-gray-100 transition-colors disabled:opacity-40 cursor-pointer"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-black">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M5 8L7 10L11 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Save List
            </button>
          </div>
        </ModalContentWrapper>
      </ModalBackdrop>
    );
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalContentWrapper onClose={onClose} maxWidthClass="max-w-[580px]">
        <div className="flex flex-col min-h-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 md:px-6 h-[64px] border-b border-[#1e1e1e] shrink-0">
            <h2 className="text-base font-semibold text-white">Choose Recipients</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/5 transition-colors text-[#525252] hover:text-white"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 px-4 md:px-6 pt-4 pb-3 shrink-0">
            <TabButton
              label="All"
              count={allCount}
              isActive={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
            />
            <TabButton
              label="Events"
              count={eventsCount}
              isActive={activeTab === 'events'}
              onClick={() => setActiveTab('events')}
            />
            <TabButton
              label="Custom"
              count={customCount}
              isActive={activeTab === 'custom'}
              onClick={() => setActiveTab('custom')}
            />
          </div>

          {/* Search */}
          <div className="px-4 md:px-6 pb-3 shrink-0">
            <div className="relative">
              <FiSearch
                size={15}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#525252]"
              />
              <input
                type="text"
                placeholder="Search mailing lists..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full h-10 pl-11 pr-4 rounded-xl bg-[#0D0D0D] border border-[#222] text-sm text-white placeholder-[#525252] focus:outline-none focus:border-[#333] transition-colors"
              />
            </div>
          </div>

          {/* Scrollable List */}
          <div className="h-[360px] max-h-[45vh] shrink-0 overflow-y-auto px-4 md:px-6 pb-4">
            <div className="space-y-1">
              {filtered.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center mb-3">
                    <FiUsersIcon />
                  </div>
                  <p className="text-sm text-[#737373]">
                    {search ? 'No lists match your search' : 'No lists in this category'}
                  </p>
                </div>
              )}
              {filtered.map((list) => {
                const isSelected = selectedUuid === list.uuid;
                return (
                  <button
                    key={list.uuid}
                    onClick={() => onConfirm(list.uuid, list.name)}
                    className={`w-full flex items-center gap-3 md:gap-4 px-3 md:px-4 py-3 md:py-3.5 rounded-xl transition-all duration-150 text-left border ${
                      isSelected
                        ? 'bg-white/10 border-white/20'
                        : 'border-transparent hover:bg-white/5 hover:border-white/5'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected
                          ? 'bg-[#00D26A] shadow-[0_0_8px_rgba(0,210,106,0.4)]'
                          : 'border-2 border-[#333333]'
                      }`}
                    >
                      {isSelected && <FiCheck size={12} className="text-black" />}
                    </div>

                    {list.is_event_linked ? (
                      <EventPoster name={list.event_title || list.name} />
                    ) : (
                      <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-[#1a1a2e] to-[#111] flex items-center justify-center flex-shrink-0 border border-[#333]/50">
                        <FiUsersIcon />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{list.name}</p>
                      <p className="text-xs text-[#737373] truncate mt-0.5">
                        {list.is_event_linked && list.event_title
                          ? list.event_title
                          : `${list.total_contacts.toLocaleString()} contact${list.total_contacts !== 1 ? 's' : ''}`}
                      </p>
                    </div>

                    {list.is_event_linked && (
                      <span className="text-xs font-medium text-[#737373] flex-shrink-0 bg-[#1a1a1a] px-2.5 py-1 rounded-full border border-[#333]/50">
                        {list.total_contacts.toLocaleString()}
                      </span>
                    )}
                    {!list.is_event_linked && list.unsubscribed_count > 0 && (
                      <span className="text-[10px] font-medium text-red-400/70 flex-shrink-0">
                        {list.unsubscribed_count} unsub
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 md:px-6 py-3.5 border-t border-[#1e1e1e] shrink-0 bg-[#080808]">
            <button
              onClick={() => setIsCreatingNewList(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#2F80ED] hover:text-[#4A9AF0] transition-colors cursor-pointer"
            >
              <FiPlus size={13} />
              Create New List
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-[#525252] hover:text-white transition-colors rounded-lg hover:bg-white/5"
            >
              Cancel
            </button>
          </div>
        </div>
      </ModalContentWrapper>
    </ModalBackdrop>
  );
};

// ─── Shared helpers ──────────────────────────────────────────────────────────

const FiUsersIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const ListOrderedIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="10" y1="6" x2="21" y2="6"></line>
    <line x1="10" y1="12" x2="21" y2="12"></line>
    <line x1="10" y1="18" x2="21" y2="18"></line>
    <path d="M4 6h1v4"></path>
    <path d="M4 10h2"></path>
    <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"></path>
  </svg>
);

const ToolbarButton = ({
  icon: Icon,
  title,
  isActive,
  onClick,
}: {
  icon: React.ElementType;
  title?: string;
  isActive?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}) => (
  <button
    onMouseDown={(e) => {
      e.preventDefault();
      onClick?.(e);
    }}
    className={`p-2 rounded transition-colors flex-shrink-0 ${
      isActive ? 'text-[#2F80ED] bg-white/10' : 'text-[#D1D1D1] hover:text-white hover:bg-white/5'
    }`}
    title={title}
    type="button"
  >
    <Icon size={16} />
  </button>
);

const ToolbarSelect = ({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (val: string) => void;
  options: { label: string; value: string }[];
}) => {
  return (
    <div className="relative flex items-center bg-transparent hover:bg-white/5 rounded text-[#D1D1D1] hover:text-white transition-colors flex-shrink-0">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-transparent pl-2 pr-7 py-1.5 text-sm text-inherit font-medium focus:outline-none cursor-pointer z-10"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-[#121212] text-[#D1D1D1]">
            {opt.label}
          </option>
        ))}
      </select>
      <div className="absolute right-2 pointer-events-none flex items-center text-[#D1D1D1]">
        <FiChevronDown size={12} />
      </div>
    </div>
  );
};

const TabButton = ({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 ${
      isActive
        ? 'bg-white text-black'
        : 'bg-[#111] text-[#737373] hover:text-white border border-[#222] hover:border-[#333]'
    }`}
  >
    {label}
    <span
      className={`tabular-nums rounded-full px-1.5 py-0.5 text-[10px] ${
        isActive ? 'bg-black/10 text-black/50' : 'bg-[#1e1e1e] text-[#525252]'
      }`}
    >
      {count}
    </span>
  </button>
);
